import { mkdir, readFile, writeFile } from "node:fs/promises";
import { dirname, relative } from "node:path";

import { CliError } from "../errors/index.js";
import { resolveExecutionContext } from "../runtime/context.js";
import { writeText } from "../runtime/output.js";

import {
  formatAgentLabel,
  isManagedAgentId,
  resolveManagedAgents,
} from "./agents.js";
import {
  buildGlobalPackageInvocation,
  isInteractiveTerminal,
  promptYesNo,
  resolvePackageManager,
  runInteractiveInvocation,
  runPassthroughInvocation,
  runQuietInvocation,
} from "./external.js";
import { buildSetupHelpText, parseSetupArgs } from "./options.js";
import { hasManagedPromptBlock, upsertManagedPromptBlock } from "./prompt-block.js";
import { buildSkillsAddInvocation, readLastSelectedAgents } from "./skills.js";
import { readSetupState, writeSetupState } from "./state.js";
import {
  getManagedCommandMarker,
  getManagedPromptFileMarker,
  renderGlobalPromptBlock,
  renderManagedCommand,
  renderManagedPromptFile,
} from "./templates.js";
import { resolveHomeDirectory } from "./home.js";
import {
  DEFAULT_PACKAGE_SPEC,
  type ManagedAgent,
  type ManagedAgentId,
  type SetupIo,
  type SetupMode,
} from "./types.js";

export async function runSetupCommand(mode: SetupMode, argv: string[], io: SetupIo) {
  const options = parseSetupArgs(mode, argv);
  if (options.help) {
    await writeText(io.stdout, buildSetupHelpText(mode));
    return 0;
  }

  const context = resolveExecutionContext(io.cwd);
  const previousState = await readSetupState(options.scope, io.cwd, io.env);
  const requestedAgents = resolveRequestedAgents(mode, options.agents, previousState?.agents);

  const effectiveOptions = {
    ...options,
    agents: requestedAgents,
  };

  const packageManager = resolvePackageManager(io.env);
  const runSkillsInvocation =
    mode === "install" && options.yes === false
      ? runInteractiveInvocation
      : mode === "upgrade"
        ? runQuietInvocation
        : runPassthroughInvocation;
  const runPackageInvocation =
    mode === "upgrade" ? runQuietInvocation : runPassthroughInvocation;

  await runSkillsInvocation(buildSkillsAddInvocation(effectiveOptions, io.env), io);
  await runPackageInvocation(
    buildGlobalPackageInvocation(packageManager, DEFAULT_PACKAGE_SPEC),
    io,
  );

  const selectedAgents = await resolveSelectedAgents(
    mode,
    effectiveOptions.agents,
    previousState?.agents,
    io.env,
  );
  const selectedPromptAgents = resolveSelectedPromptAgents(
    mode,
    options.systemPromptAgents,
    previousState?.promptAgents,
    selectedAgents,
  );
  const managedAgents = resolveManagedAgents(
    selectedAgents,
    options.scope,
    context.projectRoot,
    io.env,
  );
  const commandAgents = await writeManagedCommandFiles(managedAgents);
  const promptAgents = await maybeWriteManagedPromptBlocks(
    mode,
    managedAgents,
    io,
    selectedPromptAgents,
  );

  await writeSetupState(options.scope, io.cwd, io.env, {
    agents: selectedAgents,
    commandAgents,
    packageManager,
    promptAgents,
    scope: options.scope,
    skill: options.skill,
    source: options.source,
    updatedAt: new Date().toISOString(),
  });

  await writeSummary(
    io,
    mode,
    managedAgents,
    promptAgents,
    selectedAgents,
    buildResolvedCommand(mode, options, selectedAgents, promptAgents),
  );
  return 0;
}

function resolveRequestedAgents(
  mode: SetupMode,
  explicitAgents: readonly string[] | undefined,
  stateAgents: readonly string[] | undefined,
) {
  if (explicitAgents !== undefined) {
    return [...explicitAgents];
  }
  if (stateAgents !== undefined && stateAgents.length > 0) {
    return [...stateAgents];
  }
  if (mode === "upgrade") {
    throw new CliError(
      "UPGRADE_TARGETS_UNRESOLVED",
      "Upgrade requires a previous install state or explicit --agent flags",
    );
  }
  return undefined;
}

async function resolveSelectedAgents(
  mode: SetupMode,
  requestedAgents: readonly string[] | undefined,
  stateAgents: readonly string[] | undefined,
  env: NodeJS.ProcessEnv,
) {
  if (requestedAgents !== undefined && requestedAgents.length > 0) {
    return [...requestedAgents];
  }

  const fromSkills = await readLastSelectedAgents(env);
  if (fromSkills !== undefined && fromSkills.length > 0) {
    return [...fromSkills];
  }

  if (stateAgents !== undefined && stateAgents.length > 0) {
    return [...stateAgents];
  }

  if (mode === "install") {
    return ["codex", "claude-code"];
  }

  throw new CliError(
    "UPGRADE_TARGETS_UNRESOLVED",
    "Upgrade requires a previous install state or explicit --agent flags",
  );
}

function resolveSelectedPromptAgents(
  mode: SetupMode,
  explicitPromptAgents: readonly string[] | undefined,
  statePromptAgents: readonly ManagedAgentId[] | undefined,
  selectedAgents: readonly string[],
) {
  if (explicitPromptAgents !== undefined) {
    const invalidAgents = explicitPromptAgents.filter(
      (agentId) => !selectedAgents.includes(agentId),
    );
    if (invalidAgents.length > 0) {
      throw new CliError(
        "INVALID_PROMPT_AGENTS",
        `Prompt agents must be a subset of installed agents: ${invalidAgents.join(", ")}`,
      );
    }
    const unmanagedAgents = explicitPromptAgents.filter(
      (agentId) => !isManagedAgentId(agentId),
    );
    if (unmanagedAgents.length > 0) {
      throw new CliError(
        "INVALID_PROMPT_AGENTS",
        `Prompt injection is not managed for these agents: ${unmanagedAgents.join(", ")}`,
      );
    }
    return explicitPromptAgents.filter(
      (agentId): agentId is ManagedAgentId => isManagedAgentId(agentId),
    );
  }

  if (mode === "upgrade") {
    return [...(statePromptAgents ?? [])];
  }

  return undefined;
}

async function writeManagedCommandFiles(
  managedAgents: ReturnType<typeof resolveManagedAgents>,
) {
  const commandAgents: ManagedAgentId[] = [];

  for (const agent of managedAgents) {
    if (agent.commandTarget === undefined) {
      continue;
    }

    const nextContent = renderManagedCommand(agent.id, agent.commandTarget.kind);
    const marker = getManagedCommandMarker();

    try {
      const existing = await readFile(agent.commandTarget.path, "utf8");
      if (!existing.includes(marker)) {
        throw new CliError(
          "COMMAND_CONFLICT",
          `Refusing to overwrite unmanaged command file at ${agent.commandTarget.path}`,
        );
      }
    } catch (error) {
      if (error instanceof CliError) {
        throw error;
      }
      if ((error as NodeJS.ErrnoException).code !== "ENOENT") {
        if (error instanceof CliError) {
          throw error;
        }
        throw error;
      }
    }

    await mkdir(dirname(agent.commandTarget.path), { recursive: true });
    await writeFile(agent.commandTarget.path, nextContent, "utf8");
    commandAgents.push(agent.id);
  }

  return commandAgents;
}

async function maybeWriteManagedPromptBlocks(
  mode: SetupMode,
  managedAgents: ReturnType<typeof resolveManagedAgents>,
  io: SetupIo,
  requestedPromptAgents: readonly ManagedAgentId[] | undefined,
) {
  const candidates = managedAgents.filter(
    (agent): agent is ManagedAgent & { promptTarget: NonNullable<ManagedAgent["promptTarget"]> } =>
      agent.promptTarget !== undefined,
  );
  if (candidates.length === 0) {
    return [];
  }

  let targetAgents: ManagedAgentId[] = [];
  if (requestedPromptAgents !== undefined) {
    const unsupportedAgents = requestedPromptAgents.filter(
      (agentId) => !candidates.some((candidate) => candidate.id === agentId),
    );
    if (unsupportedAgents.length > 0) {
      throw new CliError(
        "INVALID_PROMPT_AGENTS",
        `Prompt injection is not supported for the current scope: ${unsupportedAgents.join(", ")}`,
      );
    }
    targetAgents = candidates
      .filter((agent) => requestedPromptAgents.includes(agent.id))
      .map((agent) => agent.id);
  } else if (mode === "upgrade") {
    targetAgents = await resolvePromptAgentsForUpgrade(candidates, requestedPromptAgents);
  } else if (!ioShouldPrompt(io)) {
    targetAgents = [];
  } else {
    const labels = candidates.map((agent) => formatAgentLabel(agent.id)).join(", ");
    const confirmed = await promptYesNo(
      io,
      `Inject the managed <emergent-thinking> prompt wrapper into: ${labels}?`,
    );
    targetAgents = confirmed ? candidates.map((agent) => agent.id) : [];
  }

  const block = renderGlobalPromptBlock();
  const promptFileMarker = getManagedPromptFileMarker();
  for (const agent of candidates) {
    if (!targetAgents.includes(agent.id)) {
      continue;
    }

    if (agent.promptTarget.kind === "append-block") {
      const existing = await readOptionalText(agent.promptTarget.path);
      const nextContent = upsertManagedPromptBlock(existing, block);
      await mkdir(dirname(agent.promptTarget.path), { recursive: true });
      await writeFile(agent.promptTarget.path, nextContent, "utf8");
      continue;
    }

    const nextContent = renderManagedPromptFile(agent.promptTarget.kind);
    try {
      const existing = await readFile(agent.promptTarget.path, "utf8");
      if (!existing.includes(promptFileMarker)) {
        throw new CliError(
          "PROMPT_CONFLICT",
          `Refusing to overwrite unmanaged prompt file at ${agent.promptTarget.path}`,
        );
      }
    } catch (error) {
      if (error instanceof CliError) {
        throw error;
      }
      if ((error as NodeJS.ErrnoException).code !== "ENOENT") {
        throw error;
      }
    }

    await mkdir(dirname(agent.promptTarget.path), { recursive: true });
    await writeFile(agent.promptTarget.path, nextContent, "utf8");
  }

  return targetAgents;
}

async function resolvePromptAgentsForUpgrade(
  candidates: Array<ManagedAgent & { promptTarget: NonNullable<ManagedAgent["promptTarget"]> }>,
  previousPromptAgents: readonly ManagedAgentId[] | undefined,
) {
  const requested = new Set(previousPromptAgents ?? []);
  const refreshedAgents: ManagedAgentId[] = [];
  const promptFileMarker = getManagedPromptFileMarker();

  for (const agent of candidates) {
    if (requested.has(agent.id)) {
      refreshedAgents.push(agent.id);
      continue;
    }
    const existing = await readOptionalText(agent.promptTarget.path);
    if (
      agent.promptTarget.kind === "append-block"
        ? hasManagedPromptBlock(existing)
        : existing.includes(promptFileMarker)
    ) {
      refreshedAgents.push(agent.id);
    }
  }

  return refreshedAgents;
}

function ioShouldPrompt(io: SetupIo) {
  return isInteractiveTerminal(io.stdin) && isInteractiveTerminal(io.stdout);
}

async function readOptionalText(path: string) {
  try {
    return await readFile(path, "utf8");
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") {
      return "";
    }
    throw error;
  }
}

async function writeSummary(
  io: SetupIo,
  mode: SetupMode,
  managedAgents: ReturnType<typeof resolveManagedAgents>,
  promptAgents: readonly ManagedAgentId[],
  selectedAgents: readonly string[],
  resolvedCommand: string,
) {
  const lines = [
    "",
    `${mode === "install" ? "Install" : "Upgrade"} summary:`,
    `- Selected agents: ${selectedAgents.length === 0 ? "none" : selectedAgents.map((agentId) => formatAgentLabel(agentId)).join(", ")}`,
  ];

  const commandTargets = managedAgents.filter(
    (agent): agent is ManagedAgent & { commandTarget: NonNullable<ManagedAgent["commandTarget"]> } =>
      agent.commandTarget !== undefined,
  );
  const unmanagedSelectedAgents = selectedAgents.filter(
    (agentId) => !managedAgents.some((managedAgent) => managedAgent.id === agentId),
  );
  const unsupportedCommandAgents = managedAgents.filter(
    (agent) => agent.commandTarget === undefined,
  );
  if (commandTargets.length === 0) {
    lines.push("- Agent entrypoint injection: no supported targets selected for this scope");
  } else {
    lines.push("- Agent entrypoint injection:");
    for (const agent of commandTargets) {
      lines.push(
        `  ${agent.label}: ${shortenPath(io.cwd, agent.commandTarget.path, io.env)}`,
      );
    }
  }
  if (unsupportedCommandAgents.length > 0 || unmanagedSelectedAgents.length > 0) {
    const unsupportedLabels = [
      ...unsupportedCommandAgents.map((agent) => agent.label),
      ...unmanagedSelectedAgents.map((agentId) => formatAgentLabel(agentId)),
    ];
    lines.push(
      `- Agents without managed entrypoints in this scope: ${unsupportedLabels.join(", ")}`,
    );
  }

  if (promptAgents.length === 0) {
    lines.push("- Managed prompt injection: skipped");
  } else {
    lines.push("- Managed prompt injection:");
    for (const agent of managedAgents) {
      if (!promptAgents.includes(agent.id)) {
        continue;
      }
      if (agent.promptTarget === undefined) {
        continue;
      }
      lines.push(
        `  ${agent.label}: ${shortenPath(io.cwd, agent.promptTarget.path, io.env)}`,
      );
    }
  }
  const unsupportedPromptAgents = managedAgents.filter((agent) => agent.promptTarget === undefined);
  if (unsupportedPromptAgents.length > 0 || unmanagedSelectedAgents.length > 0) {
    const unsupportedLabels = [
      ...unsupportedPromptAgents.map((agent) => agent.label),
      ...unmanagedSelectedAgents.map((agentId) => formatAgentLabel(agentId)),
    ];
    lines.push(
      `- Agents without managed prompts in this scope: ${unsupportedLabels.join(", ")}`,
    );
  }

  lines.push(`- Re-run command: ${resolvedCommand}`);

  await writeText(io.stdout, lines.join("\n"));
}

function shortenPath(cwd: string, fullPath: string, env: NodeJS.ProcessEnv) {
  const homeRelative = relative(resolveHomeDirectory(env), fullPath);
  if (!homeRelative.startsWith("..")) {
    return `~/${homeRelative}`;
  }

  const cwdRelative = relative(cwd, fullPath);
  if (!cwdRelative.startsWith("..")) {
    return `./${cwdRelative}`;
  }
  return fullPath;
}

function buildResolvedCommand(
  mode: SetupMode,
  options: {
    copy: boolean;
    runner: string;
    scope: "global" | "project";
    skill: string;
    source: string;
  },
  selectedAgents: readonly string[],
  promptAgents: readonly ManagedAgentId[],
) {
  const args = ["emergent-thinking", mode];

  args.push(`--source=${options.source}`);
  args.push(`--skill=${options.skill}`);
  args.push(options.scope === "project" ? "--project" : "--global");
  args.push(`--agents=${selectedAgents.join(",")}`);
  args.push(
    `--agents-system-prompt=${promptAgents.length === 0 ? "none" : promptAgents.join(",")}`,
  );
  if (options.copy) {
    args.push("--copy");
  }
  if (mode === "upgrade") {
    args.push("-y");
  }

  return args.join(" ");
}
